import React, { useState } from 'react'
import i18n from 'i18next'
import { useTranslation } from 'react-i18next'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'

import { Layout } from 'ui/layout/Layout'
import { AppBarCommon } from 'ui/partials/AppBar'
import { Caption, H2 } from 'ui/shorthands/Texts'
import CenteredContainer from 'ui/layout/CenteredContainer'
import { StackItem } from 'ui/layout/StackItem'
import { StackItemTextField } from 'ui/shorthands/StackItemTextField'

import { useForm, Controller, FieldValues } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button } from '@mui/material'
import placeholder from 'config/placeholder'
import type { LoginUserProps } from 'types/med'
import ForgotPasswordModal, {
  ForgotPasswordModalProps,
} from 'ui/partials/ForgotPasswordModal'
import Stack from 'ui/layout/Stack'
import InputPassword from 'ui/partials/InputPassword'
import validationSchema from 'ui/shorthands/validationSchema'

import { Api401Error } from 'app/client'

export type LoginError = 'unauthorized' | 'other'
const loginErrorText: { [key in LoginError]: string } = {
  unauthorized: i18n.t(
    'errors.認証に失敗しました。メールアドレス及びパスワードをご確認の上、もう一度お試しください。'
  ),
  other: i18n.t('errors.エラーが発生しました。もう一度お試しください。'),
}

const useStyles = makeStyles()((theme: Theme) => ({
  paper: {
    width: '100%',
    maxWidth: 672,
    backgroundColor: theme.palette.background.paper,
  },

  controlWrapper: {
    justifyContent: 'space-between',
  },
}))

export type LoginProps = {
  onSubmit: (user: LoginUserProps) => Promise<void>
  onSubmitForgotPassword: ForgotPasswordModalProps['onSubmit']
}
const Login: React.FC<LoginProps> = ({ onSubmit, onSubmitForgotPassword }) => {
  const { t } = useTranslation()
  const { classes } = useStyles()

  const schema = yup.object().shape({
    email: validationSchema.email(),
    password: yup.string().required(),
  })

  const defaultValues: FieldValues = {
    email: '',
    password: '',
  }

  const [loginError, setLoginError] = useState<LoginError | undefined>()

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FieldValues>({
    defaultValues,
    resolver: yupResolver(schema),
    mode: 'onChange',
  })

  const [showForgotModal, setShowForgotModal] = useState<boolean>(false)

  const onSubmitLogin = (data: LoginUserProps) => {
    onSubmit(data)
      .then(() => {
        setLoginError(undefined)
      })
      .catch((e) => {
        if (e instanceof Api401Error) {
          setLoginError('unauthorized')
          return
        }
        setLoginError('other')
      })
  }

  const handleCloseModal = () => {
    setShowForgotModal(false)
  }

  return (
    <Layout header={<AppBarCommon />}>
      <CenteredContainer>
        <div className={classes.paper}>
          <form
            onSubmit={handleSubmit((data) => {
              !showForgotModal && onSubmitLogin(data as LoginUserProps)
            })}
          >
            <StackItem label={<H2>{t('ログイン')}</H2>} />
            {loginError && (
              <Stack align={'center'}>
                <Caption error>{loginErrorText[loginError]}</Caption>
              </Stack>
            )}
            <StackItemTextField
              label={t('メールアドレス')}
              name={'email'}
              placeholder={placeholder().email}
              control={control}
              errorMessage={errors?.email?.message}
              fullWidth
            />
            <StackItem label={t('パスワード')} borderBottom>
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <InputPassword
                    {...field}
                    fullWidth
                    error={!!errors?.password}
                    helperText={errors?.password?.message}
                  />
                )}
              />
            </StackItem>

            <Stack className={classes.controlWrapper}>
              <Button
                color="primary"
                onClick={() => {
                  setShowForgotModal(true)
                }}
              >
                {t('パスワードを忘れた場合')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={!!Object.keys(errors).length}
              >
                {t('次へ')}
              </Button>
            </Stack>
          </form>
          <ForgotPasswordModal
            open={showForgotModal}
            onSubmit={onSubmitForgotPassword}
            onClose={handleCloseModal}
          />
        </div>
      </CenteredContainer>
    </Layout>
  )
}

export default Login
